import { accessPlaces, folderPath, sortFolders } from '@church/domain'
import {
  ACCESS_MODULE_LABELS,
  appOf,
  atLeast,
  can,
  canEditSeries,
  canEditTeamSettings,
  canGrant,
  canScheduleTeam,
  canSeeTeam,
  effectiveLevel,
  grantableLevels,
  isLadderPermission,
  LEVEL_LABELS,
  levelAnywhere,
  ORG_SCOPE,
  parseAccess,
  parseAccessGrant,
  planAllows,
  scopeTarget,
  seeingLevel,
  type Access,
  type AccessGrant,
  type AccessModule,
  type AccessTarget,
  type ChurchModule,
  type FolderTree,
  type Grants,
  type GrantScope,
  type Level,
  type LevelOf,
  type Permission,
  type PlanningLevel,
} from '@church/permissions'
import { useServiceFolders } from '../services/useServiceFolders.ts'
import { useServiceTypes } from '../services/useServices.ts'
import type { Me } from '../session/useMe.ts'
import { useSession } from '../session/useSession.ts'

/**
 * The access ladders on the client (docs/13-ACCESS.md), from `/me`. The
 * rules are `@church/permissions`'; this only wires them to the session and,
 * for a service type or folder, to the folder tree. Cosmetic, like `can()`:
 * it hides what someone cannot use, and the server refuses it anyway.
 */

/**
 * What a person holds on the ladders, and what the church's plan allows:
 * whether it includes ForServices, the ForChurch modules it leaves out, and
 * whether the account is read-only (billing).
 */
export interface LadderAccess {
  access: Access
  servicesLocked: boolean
  lockedModules: readonly string[]
  readOnly: boolean
}

/** The ladders from a `/me` payload — for route guards, which have `me` but no hooks. */
export function ladderAccess(me: Me | null | undefined): LadderAccess {
  return {
    access: parseAccess(me?.access ?? { grants: [] }),
    servicesLocked: me?.grants.lockedModules.includes('services') ?? false,
    lockedModules: me?.grants.lockedModules ?? [],
    readOnly: me?.grants.readOnly ?? false,
  }
}

const ACTION_OF = { viewer: 'read', editor: 'write', administrator: 'admin' } as const
const LEVEL_OF = { read: 'viewer', write: 'editor', admin: 'administrator' } as const

/**
 * Whether they hold at least `needed` on a ForChurch module, church-wide
 * (docs/13-ACCESS.md, step F), as far as the plan lets it through: a module
 * it leaves out is shut, and a read-only account keeps Viewer only (check-in
 * and Organization never lock). `orgWide` asks for the whole church: someone
 * limited to campuses never has it (settings, staff, imports).
 */
export function hasLevel(
  held: LadderAccess,
  module: ChurchModule,
  needed: 'viewer' | 'editor' | 'administrator',
  scope?: { orgWide?: boolean },
): boolean {
  if (scope?.orgWide && held.access.campusLimited) return false
  const locked = held.lockedModules.includes(module)
  if (locked || !planAllows({ readOnly: held.readOnly }, module, ACTION_OF[needed])) return false
  return atLeast(levelAt(held, module), needed)
}

const shut = (held: LadderAccess, module: AccessModule) =>
  held.servicesLocked && appOf(module) === 'services'

/** Their level on a module at a place (church-wide when no target). */
export function levelAt<M extends AccessModule>(
  held: LadderAccess,
  module: M,
  target: AccessTarget = ORG_SCOPE,
): LevelOf<M> | null {
  return shut(held, module) ? null : effectiveLevel(held.access, module, target)
}

/** Their highest level on a module anywhere: church-wide, or on any folder or service type. */
export function levelSomewhere<M extends AccessModule>(
  held: LadderAccess,
  module: M,
): LevelOf<M> | null {
  return shut(held, module) ? null : levelAnywhere(held.access, module)
}

/** Whether they have a place in ForServices at all: My schedule, the Music Stand. */
export const inServices = (held: LadderAccess): boolean =>
  !held.servicesLocked &&
  (held.access.churchAdmin === true ||
    held.access.grants.some((grant) => appOf(grant.module) === 'services'))

/** Whether they give access anywhere (Administrator of something). */
export const givesAccess = (held: LadderAccess): boolean =>
  held.access.churchAdmin === true ||
  held.access.grants.some((grant) => grant.level === 'administrator')

/**
 * Where ForServices opens for them: the plans when they read any beyond
 * their own, their schedule otherwise (a volunteer).
 */
export const servicesHome = (held: LadderAccess): '/services/plans' | '/my-schedule' =>
  atLeast(levelSomewhere(held, 'planning'), 'viewer') ? '/services/plans' : '/my-schedule'

/**
 * Whether a permission string holds, for lists of things that name one (the
 * menu, onboarding steps, report metrics): a ForChurch module's read, write
 * or admin through the ladder (`hasLevel`), anything else — giving, the
 * field permissions — through the role's grants, as `can()` reads them.
 */
export function allows(
  held: LadderAccess,
  me: Me | null | undefined,
  permission: Permission,
  scope?: { orgWide?: boolean },
): boolean {
  if (isLadderPermission(permission)) {
    const [module, action] = permission.split(':') as [ChurchModule, keyof typeof LEVEL_OF]
    return hasLevel(held, module, LEVEL_OF[action], scope)
  }
  if (!me) return false
  const grants: Grants = {
    permissions: me.grants.permissions as Permission[],
    campusIds: me.grants.campusIds,
  }
  return can(grants, permission, scope)
}

/** The ladders for the signed-in person, from the session alone (no fetching). */
export function useAccess() {
  const { me } = useSession()
  const held = ladderAccess(me)
  return {
    ...held,
    /**
     * ForChurch: whether they hold at least `needed` on a module church-wide,
     * as the plan allows — `access.has('people', 'editor')`. Cosmetic; the
     * server enforces.
     */
    has: (
      module: ChurchModule,
      needed: 'viewer' | 'editor' | 'administrator',
      scope?: { orgWide?: boolean },
    ) => hasLevel(held, module, needed, scope),
    /** A permission string a list names, ForChurch's through the ladder (`allows`). */
    allows: (permission: Permission, scope?: { orgWide?: boolean }) =>
      allows(held, me, permission, scope),
    /** Their level on a module church-wide, or at a target. */
    level: <M extends AccessModule>(module: M, target?: AccessTarget) =>
      levelAt(held, module, target),
    /** Their highest level on a module anywhere. */
    somewhere: <M extends AccessModule>(module: M) => levelSomewhere(held, module),
    /** Whether their highest level on a module anywhere reaches `needed`. */
    hasSomewhere: (module: AccessModule, needed: Level) =>
      atLeast<Level>(levelSomewhere(held, module), needed),
    inServices: inServices(held),
    givesAccess: givesAccess(held),
    /**
     * A church Owner or Admin by role: who invites people and removes them
     * from the church (better-auth's org management decides by role, not level).
     */
    managesMembership: held.access.churchAdmin === true,
    /** Role templates: read by whoever gives access or reads Settings… */
    readsTemplates: givesAccess(held) || hasLevel(held, 'org', 'viewer'),
    /** …and changed by an Organization Administrator. */
    managesTemplates: hasLevel(held, 'org', 'administrator'),
  }
}

/** The sections of Settings, in their order. */
export const SETTINGS_SECTIONS = [
  'profile',
  'campuses',
  'tags',
  'customFields',
  'groupTypes',
  'staff',
  'invitations',
  'roles',
  'billing',
  'developers',
  'planningCenter',
  'services',
  'account',
  'auditLog',
] as const
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]

/**
 * Which Settings sections are worth showing them (ForChurch, step F): each
 * is shown to whoever its page serves, so none opens on a refusal. The
 * church's own settings need Organization Viewer; the directory's (tags and
 * notes, custom fields) also open to a People Administrator, group types to
 * a Groups Administrator, so they can reach what they manage. API keys,
 * Planning Center, the account and the audit log need Organization
 * Administrator, church-wide; ForServices (the default level) a church-wide
 * ForServices Administrator. For route guards too (no hooks).
 */
export function settingsSections(held: LadderAccess): Record<SettingsSection, boolean> {
  const org = hasLevel(held, 'org', 'viewer')
  const keys = hasLevel(held, 'org', 'administrator', { orgWide: true })
  const directory =
    hasLevel(held, 'people', 'viewer') && (org || hasLevel(held, 'people', 'administrator'))
  return {
    profile: org,
    campuses: org,
    tags: directory,
    customFields: directory,
    groupTypes:
      hasLevel(held, 'groups', 'viewer') && (org || hasLevel(held, 'groups', 'administrator')),
    staff: org,
    invitations: org,
    roles: org,
    billing: org,
    developers: keys,
    planningCenter: keys,
    // ForServices' own settings (docs/14): a church-wide ForServices Administrator's.
    services: atLeast(levelAt(held, 'planning'), 'administrator'),
    account: keys,
    auditLog: keys,
  }
}

/** The first Settings section they may open; null when there is none (no Settings at all). */
export const firstSettingsSection = (held: LadderAccess): SettingsSection | null => {
  const shown = settingsSections(held)
  return SETTINGS_SECTIONS.find((section) => shown[section]) ?? null
}

/** Settings sections for the signed-in person (`settingsSections`). */
export function useSettingsSections() {
  const { me } = useSession()
  return settingsSections(ladderAccess(me))
}

/**
 * Which ForServices sections are worth showing them: Plans (a Viewer
 * somewhere; a Scheduled viewer only when there is a service they serve on
 * to show), Songs (songs Viewer), People (a Viewer somewhere: `peopleReach`,
 * docs/14) and Schedule (a Scheduler somewhere). A team leader is Scheduler
 * of their teams (docs/14), so they have Plans, People and Schedule.
 */
export function useServicesSections() {
  const access = useAccess()
  const reads = access.hasSomewhere('planning', 'viewer')
  // A Scheduled viewer's service types are the ones they serve on: none, no Plans.
  const types = useServiceTypes({ enabled: access.inServices && !reads })
  return {
    plans: reads || (access.inServices && (types.data?.length ?? 0) > 0),
    songs: access.hasSomewhere('songs', 'viewer'),
    // `peopleReach` is not 'none' exactly when they read somewhere.
    people: reads,
    schedule: access.hasSomewhere('planning', 'scheduler'),
    /** Sermons (search, the series planner): Editor somewhere (docs/15, "Sermon writing"). */
    sermons: access.hasSomewhere('planning', 'editor'),
    /** ForServices at all (the menu's Services entry, My schedule). */
    any: access.inServices,
  }
}

/** The folders and where each service type sits, as the rules need them. */
function treeOf(
  folders: readonly { id: string; parentId: string | null }[],
  types: readonly { id: string; folderId: string | null }[],
): FolderTree {
  return {
    folders: new Map(folders.map((folder) => [folder.id, folder.parentId])),
    serviceTypes: new Map(types.map((type) => [type.id, type.folderId])),
  }
}

/** One grant as the access screens read it: "ForServices · Planning & people — Editor · Youth". */
export interface GrantLabel {
  module: string
  level: string
  place: string
}

/**
 * The ladders in ForServices: levels on a service type or folder (through
 * the folder tree, which this fetches), what they may give and where, and
 * the places access can be given, as an indented list.
 */
export function useServiceAccess() {
  const base = useAccess()
  const { me } = useSession()
  const held: LadderAccess = {
    access: base.access,
    servicesLocked: base.servicesLocked,
    lockedModules: base.lockedModules,
    readOnly: base.readOnly,
  }
  const enabled = base.inServices
  const types = useServiceTypes({ enabled })
  const folders = useServiceFolders({ enabled })
  const typeList = types.data ?? []
  const folderList = folders.data ?? []
  const tree = treeOf(folderList, typeList)

  // An unknown service type or folder (not visible to them) has no chain:
  // only church-wide grants and its own reach it.
  const typeTarget = (id: string): AccessTarget =>
    scopeTarget(tree, { kind: 'service_type', id }) ?? { kind: 'service_type', id, folderIds: [] }
  const folderTarget = (id: string | null): AccessTarget =>
    id === null
      ? ORG_SCOPE
      : (scopeTarget(tree, { kind: 'folder', id }) ?? { kind: 'folder', id, ancestorIds: [] })

  /** Their granted planning level on a service type: its plans, teams and settings. */
  const planning = (serviceTypeId: string): PlanningLevel | null =>
    levelAt(held, 'planning', typeTarget(serviceTypeId))
  /**
   * Their level for seeing a service type's plans and teams: a team leader
   * is Scheduler where their team serves (docs/14), on top of their grants.
   */
  const seeing = (serviceTypeId: string): PlanningLevel | null =>
    held.servicesLocked ? null : seeingLevel(held.access, typeTarget(serviceTypeId))
  const leading = held.access.leading ?? []
  const leadsTeam = (teamId: string) => leading.some((team) => team.teamId === teamId)
  /** …on a folder; null is church-wide. */
  const planningInFolder = (folderId: string | null): PlanningLevel | null =>
    levelAt(held, 'planning', folderTarget(folderId))

  const placeLabel = (scope: GrantScope): string => {
    if (scope.kind === 'org') return 'Church-wide'
    if (scope.kind === 'folder') {
      const path = folderPath(folderList, scope.id)
      return path.length > 0 ? path.map((folder) => folder.name).join(' › ') : 'A folder'
    }
    return typeList.find((type) => type.id === scope.id)?.name ?? 'A service type'
  }

  return {
    ...base,
    /** The folder tree has arrived (levels on a service type or folder are final). */
    ready: !enabled || (!types.isPending && !folders.isPending),
    types: typeList,
    folders: folderList,
    tree,
    planning,
    planningInFolder,
    /**
     * Where they may put a service type or folder: the root (null) as a
     * church-wide Administrator, and each folder they administer, in tree
     * order with its path ("Main campus › Youth").
     */
    adminFolders: () => {
      const out: { id: string | null; label: string }[] = []
      if (planningInFolder(null) === 'administrator') out.push({ id: null, label: '' })
      for (const folder of sortFolders(folderList))
        if (planningInFolder(folder.id) === 'administrator')
          out.push({
            id: folder.id,
            label: folderPath(folderList, folder.id)
              .map((f) => f.name)
              .join(' › '),
          })
      return out
    },
    /**
     * Whether they hold at least `needed` on this service type, for all of
     * it. Seeing (Scheduled viewer, Viewer) counts leading a team there;
     * Scheduler and up are grants alone, since a leader schedules only their
     * own teams (`canScheduleTeam`).
     */
    can: (serviceTypeId: string, needed: PlanningLevel) =>
      atLeast(
        atLeast(needed, 'scheduler') ? planning(serviceTypeId) : seeing(serviceTypeId),
        needed,
      ),
    seeing,
    /** The teams they lead, by id: Scheduler of each (docs/14). */
    leadsTeam,
    /** The service types where they hold at least `needed` (a new plan's choices). */
    typesWhere: (needed: PlanningLevel) =>
      typeList.filter((type) =>
        atLeast(atLeast(needed, 'scheduler') ? planning(type.id) : seeing(type.id), needed),
      ),
    /**
     * A series is changed with Editor on every service type its plans are in;
     * one with no plans yet, by an Editor (the server decides who made it).
     */
    canEditSeries: (serviceTypeIds: readonly string[]) =>
      serviceTypeIds.length > 0
        ? canEditSeries([...new Set(serviceTypeIds)].map(planning))
        : base.hasSomewhere('planning', 'editor'),
    /** Changing a team: Administrator on every one of its service types. */
    canEditTeam: (team: { serviceTypeIds: readonly string[] }) =>
      canEditTeamSettings({ typeLevels: team.serviceTypeIds.map(planning) }),
    /**
     * Where a team can be put (docs/14): the service types they administer.
     * A team serves at least one (2026-10-21: there are no church-wide teams).
     */
    teamHomes: {
      types: typeList.filter((type) => atLeast(planning(type.id), 'administrator')),
      valid: (ids: readonly string[]) => ids.length > 0,
    },
    /** Whether they see a team (as the server's team lists filter them). */
    canSeeTeam: (team: { id: string; serviceTypeIds: readonly string[] }, scheduledOnIt = false) =>
      canSeeTeam({
        typeLevels: team.serviceTypeIds.map(seeing),
        scheduledOnIt,
        leadsIt: leadsTeam(team.id),
      }),
    /**
     * Scheduling onto a team. On a plan: Scheduler granted on the plan's
     * service type, or leading the team where it serves. Without a plan (the
     * schedule matrix, auto-fill): Scheduler granted on one of its service
     * types, or leading it.
     */
    canScheduleTeam: (
      team: { id: string; serviceTypeIds: readonly string[] },
      planServiceTypeId?: string,
    ) => {
      if (held.servicesLocked) return false
      if (planServiceTypeId !== undefined)
        return canScheduleTeam({
          planLevel: planning(planServiceTypeId),
          leadsIt: leadsTeam(team.id),
          servesThere: team.serviceTypeIds.includes(planServiceTypeId),
        })
      if (leadsTeam(team.id)) return true
      return team.serviceTypeIds.some((id) => atLeast(planning(id), 'scheduler'))
    },
    /** Whether they may give this grant (`canGrant`). */
    canGive: (grant: AccessGrant) => canGrant(base.access, grant, tree),
    /** Whether they may change or take away a grant someone holds (the same authority). */
    canChange: (row: {
      app: string
      module: string
      level: string
      scopeType: string
      scopeId: string | null
    }) => {
      const grant = parseAccessGrant(row)
      return grant !== null && canGrant(base.access, grant, tree)
    },
    /** A person's Permissions show to whoever gives access, and to the person themself. */
    seesPermissionsOf: (userId: string) => base.givesAccess || me?.user.id === userId,
    /**
     * Whether they see all of someone's grants (their own, or as an Owner or
     * Admin) rather than only those they could give.
     */
    isChurchAdminOrSelf: (userId: string) =>
      base.access.churchAdmin === true || me?.user.id === userId,
    /** The rungs of a module they may give at a place, lowest first. */
    grantable: (module: AccessModule, scope: GrantScope) =>
      grantableLevels(base.access, module, scope, tree),
    /** Every place access can be given, indented: the church, then folders and service types. */
    places: accessPlaces(folderList, typeList),
    placeLabel,
    /** A grant row in words, for a Permissions or Access list. */
    labelOf: (row: {
      app: string
      module: string
      level: string
      scopeType: string
      scopeId: string | null
    }): GrantLabel => {
      const grant = parseAccessGrant(row)
      const app = row.app === 'services' ? 'ForServices' : 'ForChurch'
      const moduleName =
        grant !== null ? ACCESS_MODULE_LABELS[grant.module] : row.module.replaceAll('_', ' ')
      return {
        module: `${app} · ${moduleName}`,
        level: grant !== null ? LEVEL_LABELS[grant.level] : row.level,
        place: grant !== null ? placeLabel(grant.scope) : '',
      }
    },
  }
}

export type ServiceAccess = ReturnType<typeof useServiceAccess>
